/*
  OATH CO — oath-global.js
  Phase 5: ATC intercept → cart drawer, form enhancements, QA utilities.
  Loaded via layout/theme.liquid (add: {{ 'oath-global.js' | asset_url | script_tag }})
*/

(function () {
  'use strict';

  /* ========================================================================
     1. Add-to-Cart Intercept
     Wraps the native Shopify ATC form to open cart drawer on success.
     ======================================================================== */
  document.addEventListener('submit', async function (e) {
    const form = e.target.closest('form[action="/cart/add"]');
    if (!form) return;

    e.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.setAttribute('disabled', '');
      btn.classList.add('is-loading');
    }

    try {
      const data = new FormData(form);
      const res  = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: data
      });

      if (!res.ok) throw new Error('ATC failed');

      // Fire event so cart drawer can listen
      document.dispatchEvent(new CustomEvent('oath:cart:added'));

    } catch (err) {
      console.error('Add to cart error:', err);
      // Fall back to native submit
      form.removeEventListener('submit', arguments.callee);
      form.submit();
    } finally {
      if (btn) {
        btn.removeAttribute('disabled');
        btn.classList.remove('is-loading');
      }
    }
  });

  /* ========================================================================
     2. Variant Selection — PDP (multi-option aware)
     Resolves the selected variant from ALL option groups (e.g. Type + Size),
     not just the first option. Fetches the product's variant list from the
     storefront product .js endpoint and matches on the full combination.
     Works for products with 1, 2, or 3 options.

     GALLERY IMAGE is handled separately from that async fetch (see
     applyGalleryFromTypeSelection below). Image only ever varies with the
     FIRST option group (Type: Oil/Balm/Set) on every product in this store
     — every Size within a Type shares one photo. Waiting on the async
     product.js fetch to swap the image created a real race condition: if a
     shopper clicked a variant option before that fetch resolved (very
     common — it's often the first thing people do on the page), update()
     found VARIANTS still null, matched nothing, and silently did nothing at
     all — no image, no price, no variant id. applyGalleryFromTypeSelection
     reads the already-selected Type button's own data-image/data-image-
     srcset/data-image-alt attributes (rendered server-side, present at
     load, no network dependency) and swaps the gallery instantly on every
     click, regardless of fetch timing. Price/variant-id/availability still
     go through the async VARIANTS match below, since those genuinely need
     the full Type+Size combination — image does not.

     NOTE: this variant-driven image swap only ever targets the STATIC
     product image (mainImg), never the video/embed slots below. Variant
     selection on this store has no video association, so it always
     switches the gallery back to image mode if a video happened to be
     playing — see setGalleryImage's forceImageMode call.
     ======================================================================== */
  function oathInitVariants() {
    const idInput = document.getElementById('oath-variant-id') ||
                    document.getElementById('oath-atc-variant-id');
    const groups = Array.prototype.slice.call(document.querySelectorAll('.oath-variants'));
    if (!idInput || !groups.length) return;

    let VARIANTS = null;

    // Capture the initial price string so new prices render in the same
    // currency/format the storefront is already showing (multi-currency safe).
    const priceEl     = document.querySelector('.oath-product-header__price');
    const compareEl   = document.querySelector('.oath-product-header__price--compare');
    const stickyPrice = document.querySelector('.oath-atc-sticky__price');
    const sampleMoney = (priceEl && priceEl.textContent.trim()) || '$0.00';

    // Reuse the theme's own currency symbol (from the initial rendered price)
    // and format the number for the active currency: correct decimal places
    // (KRW = 0, AUD = 2) and comma thousands separators.
    const sampleNum    = sampleMoney.match(/[\d.,]+/);
    const moneyPrefix  = sampleNum ? sampleMoney.slice(0, sampleNum.index) : '$';
    const moneySuffix  = sampleNum ? sampleMoney.slice(sampleNum.index + sampleNum[0].length) : '';
    const activeCurrency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
    let currencyDecimals = 2;
    try {
      currencyDecimals = new Intl.NumberFormat('en', { style: 'currency', currency: activeCurrency }).resolvedOptions().maximumFractionDigits;
    } catch (err) { /* keep default 2 */ }

    function formatMoney(cents) {
      if (cents == null) return '';
      const amount = cents / 100;
      const num = amount.toLocaleString('en-AU', { minimumFractionDigits: currencyDecimals, maximumFractionDigits: currencyDecimals });
      return moneyPrefix + num + moneySuffix;
    }

    function selectedValues() {
      const vals = [];
      groups.forEach(function (group) {
        const btn = group.querySelector('.oath-variants__option.is-selected') ||
                    group.querySelector('.oath-variants__option');
        if (!btn) return;
        const pos = parseInt(btn.dataset.optionPosition, 10) || 1;
        vals[pos - 1] = btn.dataset.value;
      });
      return vals;
    }

    function findVariant(vals) {
      if (!VARIANTS) return null;
      for (let i = 0; i < VARIANTS.length; i++) {
        const opts = VARIANTS[i].options || [];
        if (opts.length !== vals.length) continue;
        let ok = true;
        for (let k = 0; k < vals.length; k++) {
          if (opts[k] !== vals[k]) { ok = false; break; }
        }
        if (ok) return VARIANTS[i];
      }
      return null;
    }

    function comboAvailable(pos, value, vals) {
      const test = vals.slice();
      test[pos - 1] = value;
      const v = findVariant(test);
      return !!(v && v.available);
    }

    // Shared by both the instant DOM-driven swap and the async VARIANTS-based
    // resync: swap the main image + active thumbnail given a src/srcset/alt.
    // Always forces the gallery back into image mode first, since variant
    // selection never targets a video/embed slot.
    function setGalleryImage(src, srcset, alt) {
      if (!src) return;
      const mainImg   = document.getElementById('oath-gallery-main-img');
      const mainVideo = document.getElementById('oath-gallery-main-video');
      const mainEmbed = document.getElementById('oath-gallery-main-embed');
      const gallery   = document.getElementById('oath-gallery');
      if (!mainImg) return;

      if (window.oathGallery && typeof window.oathGallery.showImage === 'function') {
        window.oathGallery.showImage();
      } else {
        mainImg.style.display = '';
        if (mainVideo) { try { mainVideo.pause(); } catch (err) {} mainVideo.style.display = 'none'; }
        if (mainEmbed) { mainEmbed.style.display = 'none'; mainEmbed.innerHTML = ''; }
      }

      const base = src.split('?')[0];
      if (mainImg.src && mainImg.src.split('?')[0] === base) return; // already showing this image

      mainImg.classList.add('is-loading');
      mainImg.onload = function () { mainImg.classList.remove('is-loading'); };
      mainImg.src = src;
      if (srcset) mainImg.srcset = srcset;
      if (alt) mainImg.alt = alt;

      if (gallery) {
        let match = null;
        gallery.querySelectorAll('.oath-gallery__thumb').forEach(function (t) {
          const tb = (t.dataset.src || '').split('?')[0];
          if (tb && tb === base) match = t;
        });
        if (match) {
          gallery.querySelectorAll('.oath-gallery__thumb').forEach(function (t) { t.classList.remove('is-active'); });
          match.classList.add('is-active');
        }
      }
    }

    // Instant, network-independent swap: read the image straight off the
    // currently-selected button in the FIRST option group (Type). Runs on
    // every click, before the async product.js fetch has necessarily
    // resolved, so the photo always changes the moment Oil/Balm/Set is
    // clicked — clicking a Size button leaves it untouched, correctly,
    // since Size doesn't affect which photo is shown.
    function applyGalleryFromTypeSelection() {
      const firstGroup = groups[0];
      if (!firstGroup) return;
      const activeBtn = firstGroup.querySelector('.oath-variants__option.is-selected') ||
                        firstGroup.querySelector('.oath-variants__option');
      if (!activeBtn) return;
      setGalleryImage(activeBtn.dataset.image, activeBtn.dataset.imageSrcset, activeBtn.dataset.imageAlt);
    }

    function updateGallery(variant) {
      const fi = variant.featured_image;
      if (!fi || !fi.src) return;
      setGalleryImage(fi.src, null, null);
    }

    function update() {
      const vals = selectedValues();

      // Per-button availability striping based on the current cross-selection
      if (VARIANTS) {
        groups.forEach(function (group) {
          group.querySelectorAll('.oath-variants__option').forEach(function (btn) {
            const pos = parseInt(btn.dataset.optionPosition, 10) || 1;
            btn.classList.toggle('is-unavailable', !comboAvailable(pos, btn.dataset.value, vals));
          });
        });
      }

      const variant = findVariant(vals);
      if (!variant) return;

      document.querySelectorAll('#oath-variant-id, #oath-atc-variant-id').forEach(function (input) {
        input.value = variant.id;
      });

      const priceStr = formatMoney(variant.price);
      if (priceEl)     priceEl.textContent = priceStr;
      if (stickyPrice) stickyPrice.textContent = priceStr;
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.style.display = '';
          compareEl.textContent = formatMoney(variant.compare_at_price);
        } else {
          compareEl.style.display = 'none';
        }
      }

      // Redundant-but-harmless resync once the real variant list has loaded;
      // applyGalleryFromTypeSelection already put the correct image up
      // instantly on click, this just confirms it against the fetched data.
      updateGallery(variant);

      const avail = !!variant.available;
      [document.querySelector('.oath-atc__btn'), document.querySelector('.oath-atc-sticky__btn')].forEach(function (b) {
        if (!b) return;
        if (avail) {
          b.disabled = false;
          b.textContent = b.dataset.availLabel;
        } else {
          b.disabled = true;
          b.textContent = b.dataset.soldLabel;
        }
      });

      // NOTE: intentionally NOT pushing ?variant=<id> into the URL here.
      // Product URLs on this store are kept clean of the variant query
      // string by design (Jamin, Aug 2026) — variant selection still
      // updates price/image/availability/hidden id input above, it just
      // no longer rewrites the address bar.
    }

    // Remember the current (localized) button labels so we can restore them
    // when toggling between available / sold-out states.
    const soldFallback = document.documentElement.lang === 'ko' ? '품절' : 'Sold out';
    [document.querySelector('.oath-atc__btn'), document.querySelector('.oath-atc-sticky__btn')].forEach(function (b) {
      if (!b) return;
      const cur = b.textContent.trim();
      b.dataset.availLabel = (!b.disabled && cur) ? cur : (b.dataset.availLabel || 'Add to Cart');
      b.dataset.soldLabel  = b.disabled && cur ? cur : soldFallback;
    });

    // Selection clicks work immediately: the gallery swap is instant via DOM
    // data attributes; price/variant-id/striping resolve once VARIANTS has
    // loaded (update() below no-ops harmlessly if the fetch hasn't landed
    // yet, then re-runs correctly next click).
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.oath-variants__option');
      if (!btn) return;
      const group = btn.closest('.oath-variants');
      if (!group) return;
      group.querySelectorAll('.oath-variants__option').forEach(function (b) {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
      applyGalleryFromTypeSelection();
      update();
    });

    fetch(window.location.pathname.replace(/\/$/, '') + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (product) {
        VARIANTS = (product && product.variants) || [];
        update();
      })
      .catch(function () { /* leave native form behaviour */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', oathInitVariants);
  } else {
    oathInitVariants();
  }

  /* ========================================================================
     3. PDP Quantity Control
     ======================================================================== */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.oath-atc__quantity-btn');
    if (!btn) return;

    const wrap = btn.closest('.oath-atc__quantity-control');
    const val  = wrap.querySelector('.oath-atc__quantity-value');
    const input = btn.closest('form')?.querySelector('[name="quantity"]');
    if (!val) return;

    let current = parseInt(val.textContent) || 1;
    const delta = btn.textContent.trim() === '+' ? 1 : -1;
    current = Math.max(1, current + delta);

    val.textContent = current;
    if (input) input.value = current;
  });

  /* ========================================================================
     4. Sticky ATC — show/hide on scroll
     ======================================================================== */
  const stickyBar = document.querySelector('.oath-atc-sticky');
  const atcRegion = document.querySelector('.oath-atc');

  if (stickyBar && atcRegion) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { rootMargin: '-60px 0px 0px 0px' }
    );
    observer.observe(atcRegion);
  }

  /* ========================================================================
     5. Accordion (PDP + Homepage)
     ======================================================================== */
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('.oath-accordion__trigger');
    if (!trigger) return;

    const item    = trigger.closest('.oath-accordion__item');
    const isOpen  = item.classList.contains('is-open');

    // Close all in same accordion
    item.closest('.oath-accordion')
      ?.querySelectorAll('.oath-accordion__item')
      .forEach(i => {
        i.classList.remove('is-open');
        i.querySelector('.oath-accordion__trigger')?.setAttribute('aria-expanded', 'false');
      });

    // Open clicked (if it was closed)
    if (!isOpen) {
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
    }
  });

  /* ========================================================================
     6. PDP Gallery — thumbnail click (image / native video / external video)
     Supports three media kinds on the .oath-gallery__thumb buttons via
     data-type: "image" (default, existing behaviour), "video" (native
     Shopify-hosted video — sources come from a sibling <template
     id="oath-video-source-{{ media.id }}">), and "external_video"
     (YouTube/Vimeo — embedded as an iframe built from data-host +
     data-external-id). Exposes window.oathGallery.showImage() so the
     variant-selection code above can force the gallery back to image mode
     when a shopper picks a variant while a video happens to be playing.
     ======================================================================== */
  (function () {
    function getSlots(gallery) {
      return {
        mainImg:   gallery.querySelector('.oath-gallery__main-img'),
        mainVideo: gallery.querySelector('.oath-gallery__main-video'),
        mainEmbed: gallery.querySelector('.oath-gallery__main-embed')
      };
    }

    function showImage(gallery) {
      gallery = gallery || document.getElementById('oath-gallery');
      if (!gallery) return;
      const { mainImg, mainVideo, mainEmbed } = getSlots(gallery);
      if (mainImg) mainImg.style.display = '';
      if (mainVideo) {
        try { mainVideo.pause(); } catch (err) {}
        mainVideo.style.display = 'none';
      }
      if (mainEmbed) {
        mainEmbed.style.display = 'none';
        mainEmbed.innerHTML = '';
      }
    }

    // Exposed so the variant-selection code can force image mode.
    window.oathGallery = window.oathGallery || {};
    window.oathGallery.showImage = showImage;

    document.addEventListener('click', function (e) {
      const thumb = e.target.closest('.oath-gallery__thumb');
      if (!thumb) return;

      const gallery = thumb.closest('.oath-gallery');
      if (!gallery) return;
      const { mainImg, mainVideo, mainEmbed } = getSlots(gallery);
      if (!mainImg) return;

      const type = thumb.dataset.type || 'image';

      if (type === 'video' && mainVideo) {
        const tpl = document.getElementById('oath-video-source-' + thumb.dataset.videoId);
        if (tpl) {
          mainVideo.innerHTML = tpl.innerHTML;
          mainVideo.load();
          mainVideo.play().catch(function () {});
        }
        mainImg.style.display = 'none';
        mainVideo.style.display = '';
        if (mainEmbed) { mainEmbed.style.display = 'none'; mainEmbed.innerHTML = ''; }

      } else if (type === 'external_video' && mainEmbed) {
        const host  = thumb.dataset.host;
        const extId = thumb.dataset.externalId;
        let embedUrl = '';
        if (host === 'youtube') embedUrl = 'https://www.youtube.com/embed/' + extId + '?autoplay=1&rel=0';
        if (host === 'vimeo')   embedUrl = 'https://player.vimeo.com/video/' + extId + '?autoplay=1';
        mainEmbed.innerHTML = embedUrl
          ? '<iframe src="' + embedUrl + '" allow="autoplay; fullscreen" frameborder="0" allowfullscreen style="width:100%;height:100%;"></iframe>'
          : '';
        mainImg.style.display = 'none';
        if (mainVideo) { try { mainVideo.pause(); } catch (err) {} mainVideo.style.display = 'none'; }
        mainEmbed.style.display = '';

      } else {
        // image (default)
        const src    = thumb.dataset.src;
        const srcset = thumb.dataset.srcset;
        if (src) {
          mainImg.classList.add('is-loading');
          mainImg.onload = () => mainImg.classList.remove('is-loading');
          mainImg.src = src;
          if (srcset) mainImg.srcset = srcset;
        }
        showImage(gallery);
      }

      gallery.querySelectorAll('.oath-gallery__thumb').forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  })();

  /* ========================================================================
     7. Gallery Lightbox
     Skips opening when a video or embed is currently showing in the main
     slot (checked via the image slot's own display:none, set by section 6
     above) — video/embed have their own native fullscreen controls.
     ======================================================================== */
  document.addEventListener('click', function (e) {
    // Open on main image click
    const mainWrap = e.target.closest('.oath-gallery__main');
    if (mainWrap) {
      const mainImg = mainWrap.querySelector('.oath-gallery__main-img');
      if (mainImg && mainImg.style.display === 'none') return; // video/embed active, skip lightbox
      const gallery  = mainWrap.closest('.oath-gallery');
      const lightbox = gallery?.querySelector('.oath-gallery__lightbox');
      const lbImg    = lightbox?.querySelector('.oath-gallery__lightbox-img');
      if (!lightbox || !lbImg || !mainImg) return;
      lbImg.src = mainImg.src;
      lightbox.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    // Close on X or outside image
    const closeBtn = e.target.closest('.oath-gallery__lightbox-close');
    const isLightbox = e.target.closest('.oath-gallery__lightbox') && !e.target.closest('.oath-gallery__lightbox-img');
    if (closeBtn || isLightbox) {
      document.querySelector('.oath-gallery__lightbox')?.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      document.querySelector('.oath-gallery__lightbox.is-open')?.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  });

  /* ========================================================================
     8. Smooth scroll for anchor links
     ======================================================================== */
  document.addEventListener('click', function (e) {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const id = a.getAttribute('href').slice(1);
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

})();
